// MePages.tsx — /liff/me + /liff/me/settings
//
// Generic "this is who you are bound as" + "your personal preferences"
// pages. /liff/me is the canonical landing for all role placeholder cells
// (incident_reporter row1, auditor row1c1, super_admin's old "綁定我的
// LINE" cell). It surfaces:
//   - profile (display name + avatar from LINE)
//   - bound role + tenant
//   - last 5 LINE messages received from the bot (for parity with the
//     dashboard's Notifications widget)
// /liff/me/settings exposes the few user-controllable knobs we have today
// (notification toggles + locale) and clearly marks which knobs aren't
// wired yet so users don't think nothing is happening.

import React, { useEffect, useState } from "react";
import liff from "@line/liff";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import {
  MobileShell, Card, CardHeader, MobileSpinner, EmptyMobile, ListRow,
  PrimaryButton, SecondaryButton, ProfileStrip, ComingSoonCard,
} from "../../../liff/components/Mobile.js";
import { formatTaipeiDateTime, formatTaipeiTime } from "../../../lib/time.js";

const ROLE_LABEL: Record<string, string> = {
  tenant_admin: "租戶管理員", manager: "案場主管", officer: "巡邏員",
  incident_reporter: "通報員", auditor: "稽核員", super_admin: "平台管理員",
  guest: "尚未綁定",
};

interface MyNotification {
  id: string;
  at_local: string;
  title: string;
  preview: string;
}
interface MeResponse {
  notifications: MyNotification[];
}

/** Backend /v1/me/notifications real shape — different from the LIFF
 *  card's expected MyNotification. */
interface BackendNotification {
  id: string;
  kind: string;            // 'overdue' | 'incident' | 'daily' | 'handoff' | 'system_risk' | 'schedule_change' | 'coverage_gap'
  template: string | null;
  status: "queued" | "sent" | "failed" | "muted";
  incident_id: string | null;
  created_at: string;       // ISO
  sent_at: string | null;
  delivered_at: string | null;
}

const NOTIF_KIND_LABEL: Record<string, string> = {
  overdue:         "巡邏逾期",
  incident:        "事件通報",
  daily:           "每日摘要",
  handoff:         "班次交接",
  system_risk:     "系統風險",
  schedule_change: "班表變動",
  coverage_gap:    "排班缺人",
};

function reshapeNotifications(rows: BackendNotification[]): MyNotification[] {
  return rows.map((r) => ({
    id: r.id,
    at_local: formatTaipeiDateTime(r.created_at ?? null),
    title: NOTIF_KIND_LABEL[r.kind] ?? r.kind,
    preview:
      r.status === "muted" ? "（您未綁定 LINE，未發送）"
      : r.status === "failed" ? "發送失敗"
      : r.status === "queued" ? "排隊中"
      : r.template ?? "已送達",
  }));
}

/** /liff/me — bind status + recent activity. */
export function MePage(): React.ReactElement {
  const { session, profile, refresh } = useLiff();
  const [data, setData] = useState<MeResponse | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/me/notifications?tenant_id=${encodeURIComponent(session.tenant_id)}&limit=5`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as { notifications: BackendNotification[] };
          setData({ notifications: reshapeNotifications(body.notifications ?? []) });
        } else setData({ notifications: [] });
      })
      .catch(() => setData({ notifications: [] }));
  }, [session]);

  if (!session || !profile) return <MobileSpinner message="載入身分中" />;

  const expDate = new Date(session.expires_at);
  const expLocal = isNaN(expDate.getTime()) ? session.expires_at : formatTaipeiDateTime(expDate);

  return (
    <MobileShell title="我的綁定" subtitle="LINE 綁定身分">
      <ProfileStrip
        profile={{ displayName: profile.displayName, pictureUrl: profile.pictureUrl }}
        roleLabel={ROLE_LABEL[session.role] ?? session.role}
        tenantId={session.tenant_id}
      />

      <Card>
        <CardHeader title="綁定資料" />
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
          <dt className="text-text-muted">角色</dt><dd>{ROLE_LABEL[session.role] ?? session.role}</dd>
          <dt className="text-text-muted">租戶</dt><dd className="font-mono text-xs">{session.tenant_id}</dd>
          {session.user_id && (<><dt className="text-text-muted">內部 ID</dt><dd className="font-mono text-xs truncate">{session.user_id}</dd></>)}
          <dt className="text-text-muted">Session 過期</dt><dd className="text-xs">{expLocal}</dd>
        </dl>
        <div className="h-3" />
        <PrimaryButton onClick={refresh}>重新整理身分</PrimaryButton>
        <div className="h-2" />
        <SecondaryButton href="/liff/me/settings">設定</SecondaryButton>
      </Card>

      {session.role !== "officer" && session.role !== "guest" && (
        <LinkLineCard sessionToken={session.session_token} onLinked={refresh} />
      )}

      {(session.role === "officer" || session.role === "manager" || session.role === "tenant_admin") && (
        <MySitesCard sessionToken={session.session_token} tenantId={session.tenant_id} />
      )}

      <Card>
        <CardHeader title="近期通知" hint={data ? `${data.notifications.length} 筆` : ""} />
        {!data ? (
          <p className="text-xs text-text-muted">載入中…</p>
        ) : data.notifications.length === 0 ? (
          <EmptyMobile title="目前沒有新通知" />
        ) : (
          <ul className="space-y-2">
            {data.notifications.map((n) => (
              <li key={n.id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{n.title}</p>
                  <span className="text-[10px] text-text-muted">{n.at_local}</span>
                </div>
                <p className="text-[11px] text-text-muted truncate mt-0.5">{n.preview}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p className="text-[10px] text-text-muted text-center font-mono">
        liff_app: {session.liff_app} · scopes {session.scopes.length}
      </p>
    </MobileShell>
  );
}

interface SettingsState {
  notify_daily_summary: boolean;
  notify_overdue: boolean;
  notify_incident_assigned: boolean;
  locale: "zh-TW" | "en";
}

/** /liff/me/settings — personal preferences. */
export function MeSettingsPage(): React.ReactElement {
  const { session } = useLiff();
  const [state, setState] = useState<SettingsState>({
    notify_daily_summary: true,
    notify_overdue: true,
    notify_incident_assigned: true,
    locale: "zh-TW",
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState(false);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/me/settings`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as Partial<SettingsState>;
          setState((s) => ({ ...s, ...body }));
        }
      })
      .catch(() => { /* keep defaults */ });
  }, [session]);

  function update<K extends keyof SettingsState>(k: K, v: SettingsState[K]): void {
    setState((s) => ({ ...s, [k]: v }));
    setPendingSave(true);
  }

  async function save(): Promise<void> {
    if (!session) return;
    setSaving(true);
    try {
      const r = await apiFetch(
        "/v1/me/settings",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(state),
        },
        { jwt: session.session_token },
      );
      if (r.ok) {
        setSavedAt(formatTaipeiTime(new Date()));
        setPendingSave(false);
      } else {
        // Endpoint not yet shipped — keep local state visible so the user
        // sees their toggles "stuck" rather than silently lost.
        setSavedAt("(暫存於本地)");
      }
    } catch {
      setSavedAt("(離線暫存)");
    } finally {
      setSaving(false);
    }
  }

  return (
    <MobileShell title="設定" subtitle={savedAt ? `已儲存 ${savedAt}` : "通知 / 語系"}>
      <Card>
        <CardHeader title="LINE 通知" />
        <Toggle
          label="每日摘要" sublabel="當班結束時推送該日完成率與漏巡。"
          value={state.notify_daily_summary}
          onChange={(v) => update("notify_daily_summary", v)}
        />
        <Toggle
          label="漏巡警示" sublabel="案場有點位未在窗口內打卡時即時通知。"
          value={state.notify_overdue}
          onChange={(v) => update("notify_overdue", v)}
        />
        <Toggle
          label="事件指派" sublabel="您被指派為事件處理人時通知。"
          value={state.notify_incident_assigned}
          onChange={(v) => update("notify_incident_assigned", v)}
        />
      </Card>

      <Card>
        <CardHeader title="語系" />
        <div className="grid grid-cols-2 gap-2">
          {(["zh-TW", "en"] as const).map((l) => (
            <button
              key={l}
              onClick={() => update("locale", l)}
              className={`h-12 rounded-xl text-sm font-medium ${state.locale === l ? "bg-accent text-text-inverse" : "bg-bg-raised text-text-secondary"}`}
            >
              {l === "zh-TW" ? "繁體中文" : "English"}
            </button>
          ))}
        </div>
      </Card>

      <PrimaryButton onClick={save} disabled={saving || !pendingSave}>
        {saving ? "儲存中…" : pendingSave ? "儲存變更" : "已儲存"}
      </PrimaryButton>

      <ComingSoonCard
        title="更多設定"
        description="頭像、班次推播時段、暗色 / 亮色主題、登出 LINE 等選項陸續推出。"
        willShow={["勿擾時段（避免半夜推播）", "下班自動暫停通知", "事件音效自訂", "登出當前 Session（管理員強制下線）"]}
      />

      <SecondaryButton onClick={() => { try { liff.closeWindow(); } catch { /* no-op */ } }}>關閉視窗</SecondaryButton>
    </MobileShell>
  );
}

function Toggle({ label, sublabel, value, onChange }: {
  label: string; sublabel?: string; value: boolean; onChange: (v: boolean) => void;
}): React.ReactElement {
  return (
    <button
      onClick={() => onChange(!value)}
      className="w-full text-left py-3 flex items-center justify-between gap-3 border-b border-border-subtle last:border-b-0"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {sublabel && <p className="text-[11px] text-text-muted mt-0.5">{sublabel}</p>}
      </div>
      <span
        className={`shrink-0 w-11 h-6 rounded-full p-0.5 transition-colors ${value ? "bg-accent" : "bg-bg-raised"}`}
        aria-pressed={value}
      >
        <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </span>
    </button>
  );
}

/**
 * LinkLineCard — 顯示在 /liff/me 上的 「綁定 LINE」card（僅 email/SSO 帳號顯示）。
 *
 * 流程：
 *   1. liff.getIDToken() 拿目前 LINE 使用者的 id_token
 *   2. POST /v1/me/link-line { id_token }
 *   3. 後端 verify + UPDATE users.line_user_id + 跨 tenant 補
 *      line_integrations.linked_user_email
 *   4. 成功 → 提示 + 觸發 useLiff().refresh() 讓 session 帶 LINE binding
 *      狀態
 */
function LinkLineCard({ sessionToken, onLinked }: { sessionToken: string; onLinked: () => void }): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ stamped_rows: number } | null>(null);

  async function link(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      let idToken: string | null = null;
      try { idToken = liff.getIDToken(); } catch { /* not in LIFF or not logged in */ }
      if (!idToken) {
        setError("無法取得 LINE id_token。請從 LINE 內開啟此頁。");
        return;
      }
      const r = await apiFetch(
        "/v1/me/link-line",
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ id_token: idToken }) },
        { jwt: sessionToken },
      );
      if (r.ok) {
        const body = await r.json() as { stamped_rows: number };
        setSuccess({ stamped_rows: body.stamped_rows });
        onLinked();
      } else if (r.status === 409) {
        const body = await r.json().catch(() => ({} as { error?: string; message?: string })) as { error?: string; message?: string };
        setError(body.message ?? "此 LINE 帳號已綁在另一個使用者。");
      } else {
        const text = await r.text().catch(() => `HTTP ${r.status}`);
        setError(text.slice(0, 240));
      }
    } catch (e) {
      setError((e as Error).message ?? "network_error");
    } finally {
      setBusy(false);
    }
  }

  if (success) {
    return (
      <Card>
        <CardHeader title="LINE 綁定" hint="已連結" />
        <p className="text-sm text-text-secondary leading-relaxed">
          已將此 LINE 帳號連到您的 email 帳號。
          {success.stamped_rows > 0 && ` 跨 ${success.stamped_rows} 個租戶綁定資料補上 email 對應。`}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="綁定 LINE" hint="可選" />
      <p className="text-sm text-text-secondary leading-relaxed mb-3">
        將此 LINE 帳號連到您的 email 帳號 — 之後從 dashboard 或 LINE 進來都會視為同一人，
        通知 / 換班 / 排班等推播也會送到您的 LINE。
      </p>
      {error && <p className="text-xs text-danger bg-danger/10 rounded-lg p-3 mb-3">{error}</p>}
      <PrimaryButton onClick={link} disabled={busy}>
        {busy ? "綁定中…" : "立即綁定"}
      </PrimaryButton>
    </Card>
  );
}

/**
 * MySitesCard — 顯示巡邏員（或同時為巡邏員身分的管理員）目前被指派到哪些案場。
 *
 * 後端：GET /v1/me/sites?tenant_id=...
 *   - actor.kind === 'officer' → 直接用 actor.user_id 當 officer.id
 *   - 其他角色：透過 users.line_user_id → officers.line_user_id 對應
 *   - 找不到對應 officer → 回 200 空 list，不顯示卡片
 *
 * 沒有指派時顯示「請聯絡主管指派」提示，避免巡邏員以為系統壞了。
 */
interface MySite {
  site_id: string;
  site_name: string | null;
  site_risk: string | null;
  site_address: string | null;
  assigned_at: string;
}
function MySitesCard({ sessionToken, tenantId }: { sessionToken: string; tenantId: string }): React.ReactElement | null {
  const [phase, setPhase] = useState<"loading" | "ready" | "hidden">("loading");
  const [sites, setSites] = useState<MySite[]>([]);
  const [officerId, setOfficerId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/v1/me/sites?tenant_id=${encodeURIComponent(tenantId)}`, {}, { jwt: sessionToken })
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) { setPhase("hidden"); return; }
        const body = await r.json() as { items: MySite[]; officer_id: string | null };
        // 如果不是 officer 身分，後端回 officer_id=null + items=[] — 隱藏卡片
        if (!body.officer_id) { setPhase("hidden"); return; }
        setSites(body.items ?? []);
        setOfficerId(body.officer_id);
        setPhase("ready");
      })
      .catch(() => { if (!cancelled) setPhase("hidden"); });
    return () => { cancelled = true; };
  }, [sessionToken, tenantId]);

  if (phase === "hidden") return null;
  if (phase === "loading") {
    return (
      <Card>
        <CardHeader title="我可巡邏的案場" />
        <p className="text-xs text-text-muted">載入中…</p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="我可巡邏的案場" hint={`${sites.length} 個`} />
      {sites.length === 0 ? (
        <EmptyMobile
          title="尚未被指派任何案場"
          body="請聯絡此單位的管理員或主管指派您可巡邏的案場。"
        />
      ) : (
        <ul className="space-y-2">
          {sites.map((s) => (
            <li key={s.site_id}>
              <ListRow
                label={s.site_name ?? `(未命名 ${s.site_id.slice(0, 6)})`}
                sublabel={s.site_address ?? "—"}
                accessory={
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    s.site_risk === "high" ? "bg-danger/15 text-danger" :
                    s.site_risk === "medium" ? "bg-warning/15 text-warning" :
                    "bg-bg-raised text-text-muted"
                  }`}>
                    {s.site_risk === "high" ? "高風險" : s.site_risk === "medium" ? "中" : "低"}
                  </span>
                }
              />
            </li>
          ))}
        </ul>
      )}
      {officerId && (
        <p className="text-[10px] text-text-muted text-center font-mono mt-3">officer · {officerId.slice(0, 8)}…</p>
      )}
    </Card>
  );
}
